import { extend } from 'umi-request';
import { message } from 'antd';
import { history } from '@umijs/max';

import { API_CONFIG } from '@/config/api';
import { handleTokenExpiration } from '@/utils/auth';

// Use centralized base URL configuration
export const BASE_URL = API_CONFIG.BASE_URL;

const codeMessage: Record<number, string> = {
  200: 'The server successfully returned the requested data.',
  201: 'Create or modify data successfully.',
  202: 'A request has been queued in the background.',
  204: 'Delete data successfully.',
  400: 'The request was made with an error and the server did not perform any operations.',
  401: 'User does not have permission (token, username, password is incorrect).',
  403: 'User is authorized, but access is forbidden.',
  404: 'The request is for a record that does not exist.',
  406: 'The requested format is not available.',
  410: 'The requested resource is permanently deleted and will not be retrieved.',
  422: 'A validation error occurred while creating an object.',
  500: 'An error occurred on the server.',
  502: 'Gateway error.',
  503: 'The service is unavailable.',
  504: 'The gateway timed out.',
};

/**
 * Exception handler
 */
const errorHandler = (error: { response: Response }): Response => {
  const { response } = error;
  if (response && response.status) {
    const errorText = codeMessage[response.status] || response.statusText;
    const { status, url } = response;

    if (status === 401) {
      // Show confirmation dialog for token expiration
      handleTokenExpiration();
    } else {
      message.error(`Request error ${status}: ${url}\n${errorText}`);
    }
  } else if (!response) {
    message.error('Network error, please check your network connection.');
  }
  return response;
};

/**
 * Configure request instance
 */
const request = extend({
  prefix: BASE_URL,
  errorHandler,
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
request.interceptors.request.use((url, options) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    } as any;

    // Don't set Content-Type for FormData, let the browser set it automatically
    if (options.data instanceof FormData) {
      delete headers['Content-Type'];
    }

    return { url, options: { ...options, headers } };
  }
  return { url, options };
});

// Response interceptor to handle common errors
request.interceptors.response.use(async (response) => {
  if (!response.ok) {
    const data = await response.clone().json();
    message.error(data.error || 'An error occurred');
    throw new Error(data.error || 'An error occurred');
  }
  return response;
});

export default request;
